/// <reference types="vite/client" />
/**
 * V3's Redux store — root configuration only.
 *
 * Deliberately minimal: the request-creation feature owns its state, types, selectors
 * and mapping (see features/request-creation/store); this file only wires the reducers
 * and middleware together. V2's store also carried redux-persist and the international /
 * NDO-extension / faq slices — none of which V3 needs yet.
 *
 * The one transport service, the cases API (RTK Query), is registered here for its
 * reducer and middleware. No persistence is intentional: the wizard holds sensitive LE
 * data and lives in memory only (see features/request-creation/store/domesticWizardSlice.ts),
 * and the workflow resets each cases-API mutation entry once it has read the result so
 * that service holds no sensitive payload either. The auth slice is a read-model over
 * MSAL's own session-scoped cache, so it must not be persisted either.
 */
import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import domesticWizardReducer from '@/features/request-creation/store/domesticWizardSlice';
import { casesApi } from '@/features/request-creation/api/casesApi';
import caseListReducer from '@/features/case-list/store/caseListSlice';
import { caseListApi } from '@/features/case-list/api/caseListApi';
import authReducer from '@/features/auth/store/authSlice';

export const store = configureStore({
  reducer: {
    domesticWizard: domesticWizardReducer,
    caseList: caseListReducer,
    auth: authReducer,
    [casesApi.reducerPath]: casesApi.reducer,
    [caseListApi.reducerPath]: caseListApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // File objects are held transiently in the file-upload state and are not
        // serializable; the wizard never dispatches them through an action.
        ignoredPaths: ['domesticWizard.fileUpload.files'],
      },
    }).concat(casesApi.middleware, caseListApi.middleware),
  devTools: import.meta.env.DEV,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
